import * as React from "react";
import { slugify } from "../helpers";
import useTopics from "../hooks/useTopics";
import HtmlEditor from "./HtmlEditor";
import TagEditor from "./TagEditor";
import { ArticleVisibility } from "../types";
import TopicCreateModal from "./TopicCreateModal";
import useApi from "@/hooks/useApi";

interface ArticleFormModel {
  visibility: ArticleVisibility;
  searchTags: string;
  content: string;
  description: string;
  topicId: string;
  slug: string;
  title: string;
  displayPriority: number;
  botSummary: string;
  enableBotResponse: boolean;
  isPublished?: boolean;
  isFeatured?: boolean;
  lastReviewedAt?: string | null;
}

interface Props {
  modelState: { [key: string]: string[] };
  article: ArticleFormModel;
  onChange(fields: Partial<ArticleFormModel>): void;
}

const INPUT_CLASS =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const LABEL_CLASS = "block text-sm font-medium text-slate-700 mb-1";

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function generateSummary(title: string, content: string): string {
  const plainText = stripHtml(content).trim();
  if (!plainText) return "";
  const maxLen = 300;
  if (plainText.length <= maxLen) return plainText;
  const truncated = plainText.substring(0, maxLen);
  const lastSentence = truncated.lastIndexOf(".");
  if (lastSentence > 100) return truncated.substring(0, lastSentence + 1);
  return truncated + "...";
}

export default function ArticleForm(props: Props) {
  const [showTopicModal, setShowTopicModal] = React.useState(false);
  const [showSlug, setShowSlug] = React.useState(false);
  const [showVersionHistory, setShowVersionHistory] = React.useState(false);
  const [versions, setVersions] = React.useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = React.useState(false);
  const [markingReviewed, setMarkingReviewed] = React.useState(false);
  const { topics, addTopic } = useTopics();
  const api = useApi();

  return (
    <div className="grid grid-cols-3 gap-6 items-start">
      {/* ── Main content area ──────────────────────────────────── */}
      <div className="col-span-2 space-y-5">
        {/* Title */}
        <div>
          <input
            type="text"
            id="title"
            autoFocus
            placeholder="Article title…"
            className="w-full text-2xl font-semibold border-0 border-b-2 border-slate-200 hover:border-slate-300 focus:border-blue-500 focus:outline-none py-2 bg-transparent placeholder-slate-300 transition-colors"
            value={props.article.title}
            onChange={(e) => {
              props.onChange({
                title: e.currentTarget.value,
                slug: slugify(e.currentTarget.value)
              });
            }}
          />
        </div>

        {/* Content editor */}
        <div>
          <label className={LABEL_CLASS}>Content</label>
          <HtmlEditor
            value={props.article.content}
            onChange={(val: string) => props.onChange({ content: val })}
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className={LABEL_CLASS}>
            Description
          </label>
          <input
            type="text"
            id="description"
            className={INPUT_CLASS}
            placeholder="Brief summary shown in search results and article previews"
            value={props.article.description}
            onChange={(e) => props.onChange({ description: e.currentTarget.value })}
          />
          <p className="text-xs text-slate-400 mt-1">
            Shown in search results and article list previews
          </p>
        </div>

        {/* Search Tags */}
        <div>
          <label className={LABEL_CLASS}>Search Tags</label>
          <TagEditor
            value={props.article.searchTags}
            onChange={(value) => props.onChange({ searchTags: value })}
          />
          <p className="text-xs text-slate-400 mt-1">
            Keywords that help customers find this article
          </p>
        </div>
      </div>

      {/* ── Sidebar metadata panel ─────────────────────────────── */}
      <div className="col-span-1 space-y-4 sticky top-4">
        {/* Status indicator */}
        {props.article.isPublished !== undefined && (
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Status
            </h3>
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  props.article.isPublished ? "bg-green-500" : "bg-amber-400"
                }`}
              />
              <span className="text-sm font-medium text-slate-700">
                {props.article.isPublished ? "Published" : "Draft"}
              </span>
            </div>
          </div>
        )}

        {/* Organization */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Organization
          </h3>

          {/* Topic */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="topic" className={LABEL_CLASS} style={{ marginBottom: 0 }}>
                Topic
              </label>
              <button
                type="button"
                className="text-xs text-blue-600 hover:text-blue-800"
                onClick={() => setShowTopicModal(true)}
              >
                + New
              </button>
            </div>
            <select
              id="topic"
              className={INPUT_CLASS}
              value={props.article.topicId}
              onChange={(e) => props.onChange({ topicId: e.currentTarget.value })}
            >
              <option value="">Select Topic…</option>
              {(topics ?? []).map((topic) => (
                <option key={topic.Id} value={topic.Id}>
                  {topic.Name}
                </option>
              ))}
            </select>
          </div>

          {/* Visibility */}
          <div>
            <label className={LABEL_CLASS}>Visibility</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value={ArticleVisibility.Public}
                  checked={props.article.visibility === ArticleVisibility.Public}
                  onChange={() => props.onChange({ visibility: ArticleVisibility.Public })}
                  className="text-blue-600"
                />
                <span className="text-sm text-slate-700">Public</span>
                <span className="text-xs text-slate-400">— visible to everyone</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value={ArticleVisibility.Internal}
                  checked={props.article.visibility === ArticleVisibility.Internal}
                  onChange={() => props.onChange({ visibility: ArticleVisibility.Internal })}
                  className="text-blue-600"
                />
                <span className="text-sm text-slate-700">Internal</span>
                <span className="text-xs text-slate-400">— agents only</span>
              </label>
            </div>
          </div>

          {/* Display Priority */}
          <div>
            <label htmlFor="displayPriority" className={LABEL_CLASS}>
              Display Priority
            </label>
            <input
              type="number"
              id="displayPriority"
              className={INPUT_CLASS}
              value={props.article.displayPriority}
              onChange={(e) =>
                props.onChange({ displayPriority: parseInt(e.currentTarget.value) || 0 })
              }
            />
            <p className="text-xs text-slate-400 mt-1">Lower = shown first</p>
          </div>
        </div>

        {/* URL Slug (collapsible) */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <button
            type="button"
            className="flex items-center justify-between w-full"
            onClick={() => setShowSlug(!showSlug)}
          >
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              URL Slug
            </h3>
            <i className={`fa fa-chevron-${showSlug ? "up" : "down"} text-xs text-slate-400`} />
          </button>
          {showSlug && (
            <div className="mt-3">
              <input
                type="text"
                id="slug"
                className={INPUT_CLASS}
                value={props.article.slug}
                onChange={(e) => props.onChange({ slug: e.currentTarget.value })}
              />
              <p className="text-xs text-slate-400 mt-1">
                Auto-generated from title. Used in the article URL.
              </p>
            </div>
          )}
          {!showSlug && props.article.slug && (
            <p className="text-xs text-slate-500 mt-2 truncate font-mono">
              /{props.article.slug}
            </p>
          )}
        </div>

        {/* Featured Article */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Visibility
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={props.article.isFeatured ?? false}
              onChange={(e) => props.onChange({ isFeatured: e.currentTarget.checked })}
              className="rounded border-slate-300"
            />
            <div>
              <span className="text-sm font-medium text-slate-700">
                <i className="fa fa-star text-amber-400 mr-1" />
                Featured article
              </span>
              <p className="text-xs text-slate-400">
                Pinned to the top of its category on the KB homepage
              </p>
            </div>
          </label>
        </div>

        {/* Content Freshness */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Content Freshness
          </h3>
          {props.article.lastReviewedAt ? (
            <p className="text-xs text-slate-500 mb-2">
              Last reviewed:{" "}
              <span className="font-medium text-slate-700">
                {new Date(props.article.lastReviewedAt).toLocaleDateString()}
              </span>
            </p>
          ) : (
            <p className="text-xs text-slate-400 mb-2">Never reviewed</p>
          )}
          <button
            type="button"
            disabled={markingReviewed}
            className="text-xs text-green-600 hover:text-green-800 border border-green-300 rounded px-2 py-1 disabled:opacity-50"
            onClick={async () => {
              const idStr = (props.article as any).id ?? (props.article as any).Id;
              if (!idStr) return;
              setMarkingReviewed(true);
              try {
                const resp = await api.admin.post(`api/kb/articles/${idStr}/mark-reviewed`, {});
                if (resp.ok) {
                  const data = await resp.json();
                  props.onChange({ lastReviewedAt: data.lastReviewedAt });
                }
              } catch {}
              finally { setMarkingReviewed(false); }
            }}
          >
            <i className="fa fa-check-circle mr-1" />
            {markingReviewed ? "Marking…" : "Mark as reviewed today"}
          </button>
        </div>

        {/* Bot Integration */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="fa fa-robot text-blue-600" />
            <h3 className="text-sm font-semibold text-blue-800">Bot & Agent AI</h3>
          </div>
          <p className="text-xs text-blue-600 mb-3">
            When enabled, the AI bot can surface this article and deliver the summary
            to customers. Agents can also search and send the link during live chats.
          </p>

          <label className="flex items-center gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={props.article.enableBotResponse ?? false}
              onChange={(e) => props.onChange({ enableBotResponse: e.currentTarget.checked })}
              className="rounded border-slate-300"
            />
            <span className="text-sm font-medium text-slate-700">
              Enable as bot response
            </span>
          </label>

          {props.article.enableBotResponse && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Bot Summary</label>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-800 border border-blue-300 rounded px-2 py-0.5"
                  onClick={() => {
                    const summary = generateSummary(props.article.title, props.article.content);
                    if (summary) props.onChange({ botSummary: summary });
                  }}
                >
                  Auto-generate
                </button>
              </div>
              <textarea
                className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y bg-white"
                rows={4}
                placeholder="Concise summary the bot will use when responding to customers…"
                value={props.article.botSummary ?? ""}
                onChange={(e) => props.onChange({ botSummary: e.currentTarget.value })}
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-blue-500">
                  {(props.article.botSummary ?? "").length} / 500
                </span>
                {props.article.botSummary && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <i className="fa fa-check-circle" />
                    Ready
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Version History */}
        {(props.article as any).id && (
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <button
              type="button"
              className="flex items-center justify-between w-full"
              onClick={async () => {
                const next = !showVersionHistory;
                setShowVersionHistory(next);
                if (next && versions.length === 0) {
                  setVersionsLoading(true);
                  try {
                    const r = await api.admin.get(`api/kb/versions?articleId=${(props.article as any).id}`);
                    const data = await r.json();
                    setVersions(Array.isArray(data) ? data : []);
                  } catch {}
                  finally { setVersionsLoading(false); }
                }
              }}
            >
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <i className="fa fa-history mr-1" />
                Version History
              </h3>
              <i className={`fa fa-chevron-${showVersionHistory ? "up" : "down"} text-xs text-slate-400`} />
            </button>
            {showVersionHistory && (
              <div className="mt-3">
                {versionsLoading ? (
                  <p className="text-xs text-slate-400">Loading…</p>
                ) : versions.length === 0 ? (
                  <p className="text-xs text-slate-400">No saved versions yet. Versions are created each time you save.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {versions.map((v: any) => (
                      <div key={v.Id} className="flex items-center justify-between text-xs">
                        <div>
                          <span className="text-slate-600">
                            {new Date(v.SavedAt).toLocaleString()}
                          </span>
                          {v.VersionNote && (
                            <span className="text-slate-400 ml-1">— {v.VersionNote}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-800 ml-2"
                          onClick={async () => {
                            if (!confirm("Restore this version? Current content will be auto-saved first.")) return;
                            try {
                              const r = await api.admin.post(`api/kb/versions/${v.Id}/restore`, {});
                              if (r.ok) {
                                // Reload page to show restored content
                                window.location.reload();
                              }
                            } catch {}
                          }}
                        >
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showTopicModal && (
        <TopicCreateModal
          onClose={() => setShowTopicModal(false)}
          onSubmit={(topic) => {
            addTopic(topic);
            setShowTopicModal(false);
            props.onChange({ topicId: topic.Id });
          }}
        />
      )}
    </div>
  );
}
